import React, { useState, useCallback } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import { submitEquipmentCheck, updateShooterComments, updateShooter } from '../../../api';
import { getLatestScoresPerStage } from './ShooterSquadSelect';
import { getFlagSrc, getAllDivisions, getAllCategories } from '../../../utils/scoring';
import { toDivisionKey, toCategoryKey } from '../../../components/ShooterForm';
import EquipmentBelt from '../../../components/EquipmentBelt';
import { parseEquipmentBelt, getGunInfo } from '../../../utils/equipment';

const FLASH_COLORS = {
  ok: '#4caf50',
  absent: '#9e9e9e',
  'error-open': '#f44336',
  'error-noscore': '#f44336',
};

export default function EquipmentCheck() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stageIdx, squadId } = useParams();
  const [searchParams] = useSearchParams();
  const { matchId, match, shooters, scores, refreshShooters } = useOutletContext();
  const stageIdxNum = parseInt(stageIdx, 10);
  const basePath = `/${matchId}/manage/scores`;
  const splitParam = searchParams.get('split');
  const qs = splitParam ? `?split=${splitParam}` : '';

  const numStages = (match.stages || []).length;
  const latestScores = getLatestScoresPerStage(scores, numStages);
  const shootersDqed = {};
  latestScores.forEach((stageScores, sIdx) => {
    stageScores.forEach((s) => {
      if (s.dq) shootersDqed[s.shooterId] = sIdx + 1;
    });
  });

  const allSquadShooters = shooters
    .filter((s) => s.squad === squadId && !s.cancelledParticipation);
  const squadShooters = (() => {
    if (!splitParam) return allSquadShooters;
    const [part, total] = splitParam.split('-').map(Number);
    if (!part || !total || total < 2) return allSquadShooters;
    const size = Math.ceil(allSquadShooters.length / total);
    return allSquadShooters.slice((part - 1) * size, part * size);
  })();

  const [shooterIdx, setShooterIdx] = useState(0);
  const [tempChecks, setTempChecks] = useState(() => {
    const initial = {};
    squadShooters.forEach((s) => {
      if (shootersDqed[s.id] && shootersDqed[s.id] !== stageIdxNum) {
        initial[s.id] = 'absent-dq';
      } else if (match.payment !== 'none' && !s.paid) {
        initial[s.id] = 'absent-not-paid';
      }
    });
    return initial;
  });
  const [showErrorButtons, setShowErrorButtons] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [flashAction, setFlashAction] = useState(null);
  const [editDialog, setEditDialog] = useState(null); // null or { shooterId, division, category, powerFactor }
  const [editSaving, setEditSaving] = useState(false);

  const hasPassedEquipmentCheck = (shooter) => {
    return (shooter.equipmentChecks || []).some((check) => check && check.check === 'ok');
  };

  const handleEditClick = (shooter) => {
    if (hasPassedEquipmentCheck(shooter)) {
      setEditDialog({ shooterId: shooter.id, alreadyVerified: true });
    } else {
      setEditDialog({
        shooterId: shooter.id,
        division: shooter.division || '',
        category: shooter.category || '',
        powerFactor: shooter['power-factor'] || 'minor',
      });
    }
  };

  const handleEditSave = async () => {
    if (!editDialog || editDialog.alreadyVerified) return;
    setEditSaving(true);
    try {
      await updateShooter(matchId, editDialog.shooterId, {
        division: editDialog.division,
        category: editDialog.category,
        'power-factor': editDialog.powerFactor,
      });
      await refreshShooters();
      setEditDialog(null);
    } catch (err) {
      console.error('Failed to update shooter', err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleAction = useCallback(async (shooterId, action) => {
    setTempChecks((prev) => ({ ...prev, [shooterId]: action }));
    setShowErrorButtons(false);
    setNewComment('');
    setShowComments(false);
    setFlashAction({ action, shooterId });
    await new Promise((res) => setTimeout(res, 600));
    setFlashAction(null);
    setShooterIdx((prev) => Math.min(prev + 1, squadShooters.length));
  }, [squadShooters.length]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitEquipmentCheck(matchId, {
        stageIdx: stageIdxNum,
        checks: tempChecks,
      });
      await refreshShooters();
      navigate(`${basePath}/stage/${stageIdx}/squad/${squadId}/score-mode${qs}`);
    } catch (err) {
      console.error('Equipment check submit failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async (shooter) => {
    if (!newComment.trim()) return;
    const comments = [...(shooter.comments || []), {
      comment: newComment.trim(),
      name: 'RO',
      timestamp: Date.now(),
    }];
    try {
      await updateShooterComments(matchId, shooter.id, { comments });
      await refreshShooters();
      setNewComment('');
    } catch {}
  };

  const squad = (match?.squads || []).find((s) => s.id === squadId);
  const isDone = shooterIdx >= squadShooters.length;

  // Done screen
  if (isDone) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <GroupIcon sx={{ fontSize: 120, opacity: 0.5, mb: 2 }} />
        <Typography variant="h5" gutterBottom>{t('scoring.equipmentCheckDoneTitle')}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('scoring.equipmentCheckDoneBody')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 400, mx: 'auto' }}>
          <Button
            variant="contained" color="primary" size="large"
            onClick={handleSubmit} disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {t('scoring.equipmentCheckDoneButton')}
          </Button>
          <Button variant="text" onClick={() => { setShooterIdx(0); setTempChecks({}); }}>
            {t('scoring.equipmentCheckDoneRedo')}
          </Button>
        </Box>
      </Box>
    );
  }

  const currentShooter = squadShooters[shooterIdx];
  const isFlashing = flashAction && flashAction.shooterId === currentShooter?.id;
  const flashColor = isFlashing ? FLASH_COLORS[flashAction.action] : null;

  const tDiv = (d) => d ? t(`divisions.${d.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, d) : '-';
  const tCat = (c) => c ? t(`categories.${c.replace(/-([a-z])/g, (_, ch) => ch.toUpperCase())}`, c) : '-';
  const tPf = (pf) => pf ? pf.charAt(0).toUpperCase() + pf.slice(1) : '-';

  return (
    <Box>
      {/* Card */}
      <Box sx={{ position: 'relative', minHeight: 300, mb: 2 }}>
        {/* Stack hint lines behind card */}
        {shooterIdx < squadShooters.length - 1 && (
          <>
            <Box sx={{ position: 'absolute', left: 4, right: -4, top: 4, bottom: -4, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', zIndex: 1 }} />
            {shooterIdx < squadShooters.length - 2 && (
              <Box sx={{ position: 'absolute', left: 8, right: -8, top: 8, bottom: -8, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', zIndex: 0 }} />
            )}
          </>
        )}

        {(() => {
          const shooter = currentShooter;
          const check = tempChecks[shooter.id];
          const isThisFlashing = isFlashing;

          return (
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Box sx={{
                border: '1px solid',
                borderColor: isThisFlashing ? flashColor : 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                p: 3,
                position: 'relative',
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                ...(isThisFlashing && { boxShadow: `0 0 24px ${flashColor}50` }),
              }}>
                {/* Header bar inside card */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Button size="small"
                    onClick={() => navigate(`${basePath}/stage/${stageIdx}/squad/${squadId}${qs}`)}>
                    {t('common.back')}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {t('results.stageN', { n: stageIdx })} - {t('results.squadNumber')} {squad?.title || squadId}
                  </Typography>
                </Box>

                {/* Stamp overlay */}
                {check && (
                  <Box sx={{
                    position: 'absolute', top: 12, right: 12, zIndex: 10,
                    width: 80, height: 80, borderRadius: '50%',
                    border: '8px solid',
                    borderColor: check === 'ok' ? 'success.main'
                      : (check === 'error-open' || check === 'error-noscore') ? 'error.main'
                      : 'grey.500',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.9)',
                    transform: 'rotate(-15deg)',
                    animation: 'stampIn 0.3s ease-out',
                    '@keyframes stampIn': {
                      '0%': { transform: 'rotate(-15deg) scale(2)', opacity: 0 },
                      '100%': { transform: 'rotate(-15deg) scale(1)', opacity: 1 },
                    },
                  }}>
                    {check === 'ok' && <CheckIcon sx={{ fontSize: 36, color: 'success.main' }} />}
                    {(check === 'absent' || check === 'absent-dq' || check === 'absent-not-paid') && (
                      <RemoveIcon sx={{ fontSize: 36, color: 'grey.500' }} />
                    )}
                    {(check === 'error-open' || check === 'error-noscore') && (
                      <CloseIcon sx={{ fontSize: 36, color: 'error.main' }} />
                    )}
                  </Box>
                )}

                {/* DQ/Unpaid badges */}
                {shootersDqed[shooter.id] && shootersDqed[shooter.id] !== stageIdxNum && (
                  <Box sx={{
                    position: 'absolute', top: 12, right: 12,
                    bgcolor: 'black', color: 'white', px: 1.5, py: 0.5,
                    borderRadius: 1, fontWeight: 700, fontSize: '0.75rem',
                  }}>
                    DQ - {t('results.stageN', { n: shootersDqed[shooter.id] })}
                  </Box>
                )}

                {/* Shooter header: Flag + ID + Name + Club */}
                {(() => {
                  const parts = (shooter.publicId || '').split('|');
                  const displayId = parts[parts.length - 1];
                  const countryCode = parts.length > 1 ? parts[0] : null;
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        {countryCode && (
                          <img
                            alt={countryCode}
                            src={getFlagSrc({ countryCode, size: 24 })}
                            style={{ display: 'block' }}
                          />
                        )}
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {displayId}
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>
                        {shooter.name}
                      </Typography>
                      {shooter.club && (
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                          ({shooter.club})
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* Division / Category / Power Factor columns */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2.5, justifyContent: 'center' }}>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('registration.division')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {tDiv(shooter.division)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('registration.category')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {tCat(shooter.category)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('registration.powerFactor')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {tPf(shooter['power-factor'])}
                    </Typography>
                  </Box>
                </Box>

                {/* Gun information from the shooter's registration (make/model,
                    serials, license) so the RO can verify the physical gun */}
                {(() => {
                  const gun = getGunInfo(shooter);
                  return gun.length ? (
                    <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      {gun.map(({ key, label, value }) => (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, py: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{t(label)}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'end', wordBreak: 'break-word' }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null;
                })()}

                {/* Registered equipment belt (read-only) — lets the RO verify the
                    shooter's holster/pouch/magnet layout while authorizing them */}
                {(() => {
                  const belt = parseEquipmentBelt(shooter);
                  return belt ? (
                    <Box sx={{ mb: 1 }}>
                      <EquipmentBelt equipmentObj={belt} readOnly />
                    </Box>
                  ) : null;
                })()}

                {match.payment !== 'none' && !shooter.paid && (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.contrastText' }}>
                      {t('manage.unpaid')}
                    </Typography>
                  </Box>
                )}

                {/* OK Button - large, full width, green */}
                <Button
                  fullWidth variant="contained" size="large"
                  onClick={() => handleAction(shooter.id, 'ok')}
                  sx={{
                    py: 1.5, mb: 1.5,
                    borderRadius: 2,
                    fontSize: '1rem',
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#388e3c' },
                  }}
                >
                  <HowToRegIcon sx={{ fontSize: 28 }} />
                </Button>

                    {/* Confirmation text */}
                    <Typography variant="caption" color="text.secondary"
                      sx={{ display: 'block', textAlign: 'center', mb: 2.5, px: 2, lineHeight: 1.5 }}>
                      {t('scoring.shooterHere', { ro: t('scoring.ro'), shooter: shooter.name })}
                    </Typography>

                    {/* Equipment Failed sub-options overlay */}
                    {showErrorButtons && (
                      <Box sx={{
                        mb: 2, display: 'flex', flexDirection: 'column', gap: 1,
                        animation: 'fadeIn 0.2s ease',
                        '@keyframes fadeIn': {
                          '0%': { opacity: 0, transform: 'translateY(-8px)' },
                          '100%': { opacity: 1, transform: 'translateY(0)' },
                        },
                      }}>
                        <Button fullWidth variant="contained" color="warning"
                          onClick={() => handleAction(shooter.id, 'error-open')}>
                          {t('scoring.shooterEquipmentFailedMoveToOpen')}
                        </Button>
                        <Button fullWidth variant="contained" color="error"
                          onClick={() => handleAction(shooter.id, 'error-noscore')}>
                          {t('scoring.shooterEquipmentFailedMoveToNoScore')}
                        </Button>
                        <Button fullWidth variant="outlined" size="small"
                          onClick={() => setShowErrorButtons(false)}>
                          {t('common.cancel')}
                        </Button>
                      </Box>
                    )}

                    {/* Secondary action buttons */}
                    {!showErrorButtons && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-evenly', mb: 2.5 }}>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <IconButton
                            onClick={() => handleAction(shooter.id, 'absent')}
                            sx={{
                              width: 56, height: 56,
                              bgcolor: '#e0e0e0', color: '#757575',
                              '&:hover': { bgcolor: '#bdbdbd' },
                            }}
                          >
                            <PersonOffIcon sx={{ fontSize: 28 }} />
                          </IconButton>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {t('scoring.shooterNotHere')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <IconButton
                            onClick={() => handleEditClick(shooter)}
                            sx={{
                              width: 56, height: 56,
                              bgcolor: '#e1f5fe', color: '#0288d1',
                              '&:hover': { bgcolor: '#b3e5fc' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 28 }} />
                          </IconButton>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {t('scoring.shooterEdit')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                          <IconButton
                            onClick={() => setShowErrorButtons(true)}
                            sx={{
                              width: 56, height: 56,
                              bgcolor: '#ffebee', color: '#d32f2f',
                              '&:hover': { bgcolor: '#ffcdd2' },
                            }}
                          >
                            <PersonRemoveIcon sx={{ fontSize: 28 }} />
                          </IconButton>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {t('scoring.shooterEquipmentFailed')}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Comments section */}
                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t('registration.comments')}
                        </Typography>
                        <Button size="small" variant="text" sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                          onClick={() => setShowComments(!showComments)}>
                          [{showComments ? t('common.close') : t('common.open')}]
                        </Button>
                      </Box>
                      {!showComments && (
                        <Typography variant="caption" color="text.secondary">
                          {(shooter.comments || []).length > 0
                            ? `${shooter.comments.length} comment${shooter.comments.length > 1 ? 's' : ''}`
                            : `- ${t('common.none')} -`
                          }
                        </Typography>
                      )}
                      <Collapse in={showComments}>
                        <Box sx={{ mt: 1 }}>
                          {(shooter.comments || []).length > 0 ? (
                            <Box sx={{ mb: 1.5 }}>
                              {shooter.comments.map((c, ci) => (
                                <Typography key={ci} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                                  &bull; {c.comment} <em>({c.name})</em>
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              - {t('common.none')} -
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small" fullWidth
                              placeholder={t('registration.comments')}
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                            />
                            <Button size="small" variant="outlined"
                              onClick={() => handleAddComment(shooter)}>
                              +
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </Box>

                    {/* Previous / Next navigation inside card */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Button disabled={shooterIdx === 0} size="small" sx={{ textTransform: 'uppercase', fontWeight: 500 }}
                        onClick={() => { setShooterIdx(shooterIdx - 1); setShowErrorButtons(false); setShowComments(false); setNewComment(''); }}>
                        {t('scoring.previousShooter')}
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {shooterIdx + 1} / {squadShooters.length}
                      </Typography>
                      <Button disabled={shooterIdx >= squadShooters.length - 1} size="small"
                        sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                        onClick={() => { setShooterIdx(shooterIdx + 1); setShowErrorButtons(false); setShowComments(false); setNewComment(''); }}>
                        {t('scoring.nextShooter')}
                      </Button>
                    </Box>
              </Box>
            </Box>
          );
        })()}
      </Box>

      {/* Edit details dialog */}
      <Dialog open={!!editDialog} onClose={() => !editSaving && setEditDialog(null)} maxWidth="xs" fullWidth>
        {editDialog?.alreadyVerified ? (
          <>
            <DialogTitle>{t('scoring.shooterEdit')}</DialogTitle>
            <DialogContent>
              <Typography>{t('scoring.editAlreadyVerified')}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialog(null)}>{t('common.close')}</Button>
            </DialogActions>
          </>
        ) : editDialog && (
          <>
            <DialogTitle>{t('scoring.shooterEdit')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
              <FormControl fullWidth>
                <InputLabel>{t('registration.division')}</InputLabel>
                <Select
                  value={editDialog.division}
                  label={t('registration.division')}
                  onChange={(e) => setEditDialog({ ...editDialog, division: e.target.value })}
                >
                  {getAllDivisions({ matchType: match.matchType }).map((d) => (
                    <MenuItem key={d} value={d}>{t(`divisions.${toDivisionKey(d)}`, d)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('registration.category')}</InputLabel>
                <Select
                  value={editDialog.category}
                  label={t('registration.category')}
                  onChange={(e) => setEditDialog({ ...editDialog, category: e.target.value })}
                >
                  {getAllCategories({ matchType: match.matchType }).map((c) => (
                    <MenuItem key={c} value={c}>{t(`categories.${toCategoryKey(c)}`, c)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('registration.powerFactor')}</InputLabel>
                <Select
                  value={editDialog.powerFactor}
                  label={t('registration.powerFactor')}
                  onChange={(e) => setEditDialog({ ...editDialog, powerFactor: e.target.value })}
                >
                  <MenuItem value="minor">Minor</MenuItem>
                  <MenuItem value="major">Major</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialog(null)} disabled={editSaving}>{t('common.cancel')}</Button>
              <Button variant="contained" onClick={handleEditSave} disabled={editSaving}
                startIcon={editSaving ? <CircularProgress size={16} /> : null}>
                {t('common.save')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
